import MyContext from './AppContext';
import { NavigationView } from '@/shared/constants/navigation-view.enum';
import { navigationViewFromApiVisao } from '@/shared/navigation/navigation-view.utils';
import { subscribeAuthSession } from '@/modules/system/auth/application/auth-session.utils';
import {
  getLoginTokenFromStorage,
  getUserIdFromLoginJwt,
  isLogedIn,
  isPublicCandidateRoute,
  SetPermissoesFromRoleLevels,
  deveExibirNavbarLoginTokenInterno,
} from '@/modules/system/auth/application/auth.service';
import { GetAuthMe } from '@/modules/system/usuario/usuario.service';
import { CorregedoriaCheck } from '@/modules/reports/application/relatorios-corregedoria.service';
import { BuscarDefensoresVinculadosAssessor } from '@/modules/reports/application/relatorios-atividades.service';
import { ReactNode, useEffect, useState } from 'react';
import { UsuarioConfigs } from '../types/usuario_config';

interface MyProviderProps {
  children: ReactNode;
}

export const initialState: UsuarioConfigs = {
  username: '',
  cor_fonte_zebrado_tabela: '',
  cor_fundo_zebrado_tabela: '',
  nome: '',
  navbar: false,
  userId: undefined,
  navigationViewOverride: undefined,
};

export const MyProvider: React.FC<MyProviderProps> = ({ children }) => {
  const [state, setStateInternal] = useState<UsuarioConfigs>(() => {
    const base = UsuarioConfigs.GetConfigs() || initialState;
    if (base.userId == null) {
      const fromJwt = getUserIdFromLoginJwt();
      if (fromJwt != null) return { ...base, userId: fromJwt };
    }
    return base;
  });
  const [sessionReady, setSessionReady] = useState(() => !isLogedIn());
  const [authVisao, setAuthVisao] = useState<NavigationView | null>(null);
  const [ehDefensorDiretor, setEhDefensorDiretor] = useState(false);
  const [isCorregedoria, setIsCorregedoria] = useState(false);
  const [assessorPodeEditarRelatorio, setAssessorPodeEditarRelatorio] = useState(false);
  const [temPainelGerencial, setTemPainelGerencial] = useState(false);
  const [authEpoch, setAuthEpoch] = useState(0);

  useEffect(() => subscribeAuthSession(() => setAuthEpoch((n) => n + 1)), []);

  const setState: React.Dispatch<React.SetStateAction<UsuarioConfigs>> = (
    value,
  ) => {
    setStateInternal((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      UsuarioConfigs.SetConfigs(next);
      return next;
    });
  };

  useEffect(() => {
    const token = getLoginTokenFromStorage();

    if (!token) {
      setAuthVisao(null);
      setEhDefensorDiretor(false);
      setIsCorregedoria(false);
      setTemPainelGerencial(false);
      setSessionReady(true);
      return;
    }

    // Portal do candidato: não carrega sessão interna (evita 401 e redirecionamento).
    if (isPublicCandidateRoute()) {
      setAuthVisao(null);
      setEhDefensorDiretor(false);
      setIsCorregedoria(false);
      setTemPainelGerencial(false);
      setSessionReady(true);
      return;
    }

    setSessionReady(false);

    let cancel = false;
    let visaoAplicada = false;
    void GetAuthMe()
      .then(async (me) => {
        if (cancel || !me) return;
        const visaoApi = navigationViewFromApiVisao(me.visao);
        if (visaoApi == null) return;

        setAuthVisao(visaoApi);
        setEhDefensorDiretor(Boolean(me.ehDefensorDiretor));
        setTemPainelGerencial(Boolean(me.temPainelGerencial));
        try {
          const corregedoria = await CorregedoriaCheck();
          if (cancel) return;
          setIsCorregedoria(corregedoria);
        } catch {
          if (cancel) return;
          setIsCorregedoria(false);
        }
        try {
          const cpf = me.cpf?.trim();
          if (cpf) {
            const vinculados = await BuscarDefensoresVinculadosAssessor(cpf);
            if (cancel) return;
            setAssessorPodeEditarRelatorio(vinculados.length > 0);
          } else if (!cancel) {
            setAssessorPodeEditarRelatorio(false);
          }
        } catch {
          if (cancel) return;
          setAssessorPodeEditarRelatorio(false);
        }
        if (cancel) return;
        if (me.permissoes?.length) {
          SetPermissoesFromRoleLevels(me.permissoes);
        }
        setState((prev) => {
          const next: UsuarioConfigs = {
            ...prev,
            userId: prev.userId ?? me.userId,
            nome: prev.nome?.trim() ? prev.nome : me.nome?.trim() || prev.nome,
            cpf: prev.cpf?.trim() ? prev.cpf : me.cpf?.trim() || prev.cpf,
            pessoaId: me.pessoaId ?? prev.pessoaId,
            username: prev.username?.trim()
              ? prev.username
              : me.username ?? prev.username,
            cor_fonte_zebrado_tabela:
              prev.cor_fonte_zebrado_tabela || me.corletratabela || '',
            cor_fundo_zebrado_tabela:
              prev.cor_fundo_zebrado_tabela || me.corfundotabela || '',
            navigationViewOverride: visaoApi ?? prev.navigationViewOverride,
            navbar:
              prev.navbar ||
              deveExibirNavbarLoginTokenInterno(me.visao, me.userId),
          };
          return next;
        });
        visaoAplicada = true;
      })
      .catch(() => {
        /* sessão inválida: token removido em GetAuthMe quando 401 */
      })
      .finally(() => {
        if (cancel) return;
        const userId = state.userId ?? getUserIdFromLoginJwt();
        if (visaoAplicada || userId == null) setSessionReady(true);
      });

    return () => {
      cancel = true;
    };
  }, [state.userId, authEpoch]);

  return (
    <MyContext.Provider
      value={{
        state,
        setState,
        sessionReady,
        authVisao,
        ehDefensorDiretor,
        isCorregedoria,
        assessorPodeEditarRelatorio,
        temPainelGerencial,
      }}
    >
      {children}
    </MyContext.Provider>
  );
};
